import * as L from 'leaflet';
import { HttpClient } from '@angular/common/http';
import { FeatureDisplayItemGenerator } from './featureDisplayItemGenerator';
import { FeatureDisplayItem } from './featureDisplayItem';
import { MapLayer } from '../layers/mapLayer.abstract';

/**
 * This class allows you to optionally define:
 * - how a feature service is called, or use default caller but define the url generation,
 * - how the call response is handled and processed,
 * - how the feature details are extracted,
 * - how the feature details are displayed.
 * This is done by setting the dynamic functions that are used in the process
 */
export class JsonFeatureIdentifier implements FeatureDisplayItemGenerator {
  // public JsonFeatureIdentifier = true; // used for type evaluation

  // used to call service for feature data
  protected featureServiceCaller = this.defaultFeatureServiceCaller.bind(this) as (
    http: HttpClient,
    layer: MapLayer,
    clickEvent: L.LeafletMouseEvent,
  ) => Promise<Record<string, unknown>>;
  // used (by the default feature service caller) to generate a url to get the feature details
  protected urlCreator: (layer: MapLayer, clickEvent: L.LeafletMouseEvent) => null | string;
  // used to extract the array of feature details from the response
  protected responseResultsExtractor = this.defaultResponseResultsExtractor.bind(this) as (
    res: Record<string, unknown>,
  ) => Array<Record<string, unknown>>;
  // used to generate a map of key vale pairs of details to display for a feature
  protected resultDetailsExtractor = this.defaultResultDetailsExtractor.bind(this) as (
    featureDetails: Record<string, unknown>,
  ) => Map<string, string>;
  // used to create display  for item title and details
  protected itemDisplayGenerator = this.defaultDisplayElementGenerator.bind(this) as (
    detailsMap: Map<string, string>,
    layer: MapLayer,
  ) => HTMLElement;

  constructor(
    protected layer: MapLayer,
    urlCreator: (layer: MapLayer, clickEvent: L.LeafletMouseEvent) => null | string,
  ) {
    this.urlCreator = urlCreator;
  }

  /**
   * Called by the map component to get feature display element generation functions
   * @param http http client for use if required
   * @param mapState current MapState object
   * @param clickEvent Map click mouse event
   */
  public getFeatureDisplayItems(clickEvent: L.LeafletMouseEvent, http: HttpClient): Promise<Array<FeatureDisplayItem>> {
    return this.featureServiceCaller(http, this.layer, clickEvent)
      .then((res: Record<string, unknown>) => {
        const mapFeatureItems = new Array<FeatureDisplayItem>();
        const features = this.responseResultsExtractor(res);
        if (features != null) {
          features.forEach((element: Record<string, unknown>) => {
            mapFeatureItems.push(
              new FeatureDisplayItem(element, () => {
                const detailsMap = this.resultDetailsExtractor(element);
                return this.itemDisplayGenerator(detailsMap, this.layer);
              }),
            );
          });
        }
        return mapFeatureItems;
      })
      .catch(() => {
        console.warn(`ERROR processing feature identify info for layer: ${this.layer.name}`);
        return new Array<FeatureDisplayItem>();
      });
  }

  /**
   * set this function to define how a service is called
   * @param featureServiceCaller Function that will return a function that returns feature details
   */
  public setFeatureServiceCaller(
    featureServiceCaller: (
      http: HttpClient,
      layer: MapLayer,
      clickEvent: L.LeafletMouseEvent,
    ) => Promise<Record<string, unknown>>,
  ): this {
    this.featureServiceCaller = featureServiceCaller;
    return this;
  }

  /**
   * @param urlCreator Function that defines how the the url is generated for
   * the default featureServiceCaller method
   */
  public setUrlCreator(urlCreator: (layer: MapLayer, clickEvent: L.LeafletMouseEvent) => string): this {
    this.urlCreator = urlCreator;
    return this;
  }

  /**
   * @param responseResultsExtractor function that defines how the feature response is
   * split into individual feature objects
   */
  public setResponseResultsExtractor(
    responseResultsExtractor: (res: Record<string, unknown>) => Array<Record<string, unknown>>,
  ): this {
    this.responseResultsExtractor = responseResultsExtractor;
    return this;
  }

  /**
   * @param resultDetailsExtractor function that defines how the required attributes of
   * the individual feature objects are extracted
   */
  public setResultDetailsExtractor(
    resultDetailsExtractor: (featureDetails: Record<string, unknown>) => Map<string, string>,
  ): this {
    this.resultDetailsExtractor = resultDetailsExtractor;
    return this;
  }

  /**
   * @param itemDisplayGenerator function that defines how the required feature attributes
   * are displayed
   */
  public setItemDisplayGenerator(
    itemDisplayGenerator: (itemDetails: Map<string, string>, layer: MapLayer) => HTMLElement,
  ): this {
    this.itemDisplayGenerator = itemDisplayGenerator;
    return this;
  }

  // default featureServiceCaller
  // uses the set urlCreator function
  protected defaultFeatureServiceCaller(
    http: HttpClient,
    layer: MapLayer,
    clickEvent: L.LeafletMouseEvent,
  ): Promise<null | Record<string, unknown>> {
    const url = this.urlCreator(layer, clickEvent);
    return Promise.resolve<null | Record<string, unknown>>(
      null == url
        ? null
        : http
            .get(url)
            .toPromise()
            .then((res: Record<string, unknown>) => res)
            .catch(() => {
              console.warn(`ERROR retrieving feature identify info for layer: ${layer.name}`);
              return null;
            }),
    );
  }

  protected defaultResponseResultsExtractor(res: Record<string, unknown>): Array<Record<string, unknown>> {
    return res.features as Array<Record<string, unknown>>;
  }

  protected defaultResultDetailsExtractor(featureDetails: Record<string, unknown>): Map<string, string> {
    const returnMap = new Map<string, string>();
    const properties = featureDetails.properties as Record<string, unknown>;
    Object.keys(properties).forEach((key: string) => {
      returnMap.set(key, String(properties[key]));
    });
    return returnMap;
  }

  // default itemDisplayGenerator
  // displays feature details in a table
  protected defaultDisplayElementGenerator(
    detailsMap: Map<string, string>,
     
    layer: MapLayer,
  ): HTMLElement {
    const tbodyElement = document.createElement('tbody');

    detailsMap.forEach((value: string, key: string) => {
      const keyContent = document.createElement('td');
      keyContent.innerHTML = key;
      const valueContent = document.createElement('td');
      valueContent.innerHTML = value;

      const row = document.createElement('tr');
      row.appendChild(keyContent);
      row.appendChild(valueContent);
      tbodyElement.appendChild(row);
    });

    const tableElement = document.createElement('table');
    tableElement.classList.add('table');
    tableElement.classList.add('table-striped');
    tableElement.appendChild(tbodyElement);

    const titleElement = document.createElement('h5');
    titleElement.classList.add('popup-title');
    titleElement.innerHTML = layer.name;

    const displayElement = document.createElement('div');
    displayElement.appendChild(titleElement);
    displayElement.appendChild(tableElement);
    displayElement.classList.add('table-wrapper');

    return displayElement;
  }
}

export class FeatureIdentifyDisplayItem {
  constructor(public title: HTMLElement, public content: HTMLElement) {}
}
